import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { EnvelopeSimple, PaperPlaneTilt, Eye, Trash, Plus, Users, FileArrowUp, Warning, CheckCircle } from "@phosphor-icons/react";

const CHIP_TONES = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  probation: "bg-amber-100 text-amber-800 border-amber-300",
  notice: "bg-orange-100 text-orange-800 border-orange-300",
  resigned: "bg-rose-100 text-rose-800 border-rose-300",
  exited: "bg-zinc-100 text-zinc-700 border-zinc-300",
  default: "bg-zinc-100 text-zinc-800 border-zinc-300",
};

function MultiSelect({ label, options, values, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={testid}
          className="w-full rounded-sm border border-zinc-300 bg-white px-3 py-2 text-left text-sm hover:border-zinc-400"
        >
          {values.length === 0
            ? <span className="text-zinc-500">Any {label.toLowerCase()}</span>
            : <span className="text-zinc-900">{values.length} selected</span>}
        </button>
        {open && (
          <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-sm border border-zinc-300 bg-white shadow-lg">
            {options.length === 0 && <div className="p-3 text-xs text-zinc-500">No options</div>}
            {options.map((opt) => (
              <label key={opt} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-zinc-50">
                <Checkbox checked={values.includes(opt)} onCheckedChange={() => toggle(opt)} />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
            <div className="border-t border-zinc-200 p-2">
              <button onClick={() => setOpen(false)} className="text-xs text-emerald-700 hover:underline">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`rounded-md border p-3 ${tone || "border-zinc-200 bg-white"}`}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold">{value}</div>
    </div>
  );
}

export default function BulkLetters() {
  const { user, tenantId } = useAuth();
  const isAdmin = ["super_admin", "company_admin", "hr"].includes(user?.role);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");

  // Facets
  const [facets, setFacets] = useState({ departments: [], grades: [], bands: [], locations: [], statuses: [], total_scope: 0 });

  // Mode + filters
  const [mode, setMode] = useState("filter"); // filter | ids
  const [filters, setFilters] = useState({ departments: [], grades: [], bands: [], locations: [], statuses: ["active"], doj_from: "", doj_to: "" });
  const [manualIds, setManualIds] = useState([]); // for mode=ids

  // Recipients
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState({}); // {id: true}
  const [truncated, setTruncated] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Variables
  const [globalVars, setGlobalVars] = useState([{ k: "effective_date", v: "" }, { k: "new_ctc", v: "" }]);
  const [perEmpMap, setPerEmpMap] = useState({}); // {emp_id: {var: val}}
  const [sampleVars, setSampleVars] = useState([]);
  const [csvSummary, setCsvSummary] = useState(null);

  // Delivery
  const [deliver, setDeliver] = useState({ pdf_vault: true, email: true, notification: true });
  const [subjectOverride, setSubjectOverride] = useState("");
  const [emailPrefix, setEmailPrefix] = useState("");

  // Preview render
  const [renderOpen, setRenderOpen] = useState(false);
  const [renderData, setRenderData] = useState(null);

  // Result + history
  const [issuing, setIssuing] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [history, setHistory] = useState([]);

  const params = useMemo(
    () => (user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {}),
    [user?.role, tenantId],
  );

  const loadTemplates = async () => {
    try {
      const [legacy, admin] = await Promise.all([
        api.get("/letter-templates", { params }).then(({ data }) => data).catch(() => []),
        api.get("/admin/letter-templates", { params }).then(({ data }) => data).catch(() => []),
      ]);
      const byKey = {};
      for (const t of legacy) byKey[t.key] = t;
      for (const t of admin) byKey[t.kind] = { id: t.id, key: t.kind, title: t.title, body: t.body, source: "admin" };
      const list = Object.values(byKey);
      setTemplates(list);
      if (!templateKey && list.length) setTemplateKey(list[0].key);
    } catch (e) { /* silent */ }
  };

  const loadFacets = async () => {
    try {
      const { data } = await api.get("/bulk-letters/facets", { params });
      setFacets(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/bulk-letters/history", { params: { ...params, limit: 20 } });
      setHistory(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadTemplates(); loadFacets(); loadHistory(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const previewRecipients = async () => {
    setPreviewLoading(true);
    try {
      const payload = mode === "filter"
        ? { ...params, mode: "filter", filters }
        : { ...params, mode: "ids", employee_ids: manualIds };
      const { data } = await api.post("/bulk-letters/preview-recipients", payload);
      setRecipients(data.recipients);
      setTruncated(!!data.truncated);
      // Auto-select everyone in the preview
      const sel = {}; for (const r of data.recipients) sel[r.id] = true;
      setSelectedIds(sel);
      if (data.recipients.length === 0) toast.warning("No recipients matched. Try widening the filters.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPreviewLoading(false); }
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (tenantId && user?.role === "super_admin") form.append("tenant_id", tenantId);
    try {
      const { data } = await api.post("/bulk-letters/parse-csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      setManualIds(data.employee_ids);
      setPerEmpMap(data.per_employee_context || {});
      setSampleVars(data.sample_variables || []);
      setCsvSummary({ matched: data.matched_count, unmatched: data.unmatched_count, unmatchedCodes: data.unmatched_codes });
      setMode("ids");
      toast.success(`Matched ${data.matched_count} employees from CSV`);
      // Auto-preview
      const payload = { ...params, mode: "ids", employee_ids: data.employee_ids };
      const previewResp = await api.post("/bulk-letters/preview-recipients", payload);
      setRecipients(previewResp.data.recipients);
      setTruncated(!!previewResp.data.truncated);
      const sel = {}; for (const r of previewResp.data.recipients) sel[r.id] = true;
      setSelectedIds(sel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const previewRender = async () => {
    const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
    if (ids.length === 0) return toast.error("Select at least one recipient");
    if (!templateKey) return toast.error("Pick a template");
    const targetId = ids[0];
    const globalCtx = Object.fromEntries(globalVars.filter((r) => r.k && r.v).map((r) => [r.k, r.v]));
    const empCtx = perEmpMap[targetId] || {};
    try {
      const { data } = await api.post("/bulk-letters/preview-render", {
        ...params, template_key: templateKey, employee_id: targetId,
        global_context: globalCtx, employee_context: empCtx,
      });
      setRenderData(data);
      setRenderOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitIssue = async () => {
    const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
    if (ids.length === 0) return toast.error("Select at least one recipient");
    if (!templateKey) return toast.error("Pick a template");
    if (!window.confirm(`Issue "${templates.find((t) => t.key === templateKey)?.title || templateKey}" to ${ids.length} employee(s)?`)) return;

    const globalCtx = Object.fromEntries(globalVars.filter((r) => r.k && r.v).map((r) => [r.k, r.v]));
    // Filter perEmpMap to only selected IDs
    const perCtx = {};
    for (const id of ids) if (perEmpMap[id]) perCtx[id] = perEmpMap[id];

    setIssuing(true);
    try {
      const { data } = await api.post("/bulk-letters/issue", {
        ...params, template_key: templateKey,
        mode: "ids", employee_ids: ids,
        global_context: globalCtx, per_employee_context: perCtx,
        subject_override: subjectOverride || null,
        email_body_prefix: emailPrefix || null,
        deliver, dry_run: false,
      });
      setLastRun(data);
      toast.success(`Issued: ${data.counts.generated} letters · ${data.counts.email_sent} emails sent · ${data.counts.notified} notifications`);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setIssuing(false); }
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  const toggleAll = (val) => {
    const sel = {}; for (const r of recipients) sel[r.id] = val;
    setSelectedIds(sel);
  };

  return (
    <div className="space-y-6" data-testid="bulk-letters-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy">Documents · Bulk</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Bulk letter issuance</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Increment, appraisal, confirmation, warning — issue any HR letter to many employees at once with per-employee variables.
        </p>
      </div>

      <Tabs defaultValue="compose">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="compose" className="rounded-sm" data-testid="bulk-tab-compose">1. Compose &amp; select</TabsTrigger>
          <TabsTrigger value="deliver" className="rounded-sm" data-testid="bulk-tab-deliver">2. Deliver</TabsTrigger>
          <TabsTrigger value="history" className="rounded-sm" data-testid="bulk-tab-history">History</TabsTrigger>
        </TabsList>

        {/* ─── STEP 1 ───────────────────────────────────────────── */}
        <TabsContent value="compose" className="mt-4 space-y-6">
          {/* Template */}
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div className="text-sm font-semibold text-zinc-700">Letter template</div>
            <Select value={templateKey} onValueChange={setTemplateKey}>
              <SelectTrigger className="rounded-sm max-w-md" data-testid="bulk-template-select">
                <SelectValue placeholder="Pick a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.title}
                    {t.source === "admin" && " · customized"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selection */}
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-zinc-700">Select recipients</div>
                <div className="text-xs text-zinc-500">Scope pool: {facets.total_scope} employee(s) visible to you</div>
              </div>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="rounded-sm">
                  <TabsTrigger value="filter" className="rounded-sm text-xs" data-testid="bulk-mode-filter">Filter</TabsTrigger>
                  <TabsTrigger value="ids" className="rounded-sm text-xs" data-testid="bulk-mode-ids">Pick manually / CSV</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {mode === "filter" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MultiSelect label="Departments" options={facets.departments} values={filters.departments} onChange={(v) => setFilters({ ...filters, departments: v })} testid="filter-department" />
                <MultiSelect label="Grades" options={facets.grades} values={filters.grades} onChange={(v) => setFilters({ ...filters, grades: v })} testid="filter-grade" />
                <MultiSelect label="Bands" options={facets.bands} values={filters.bands} onChange={(v) => setFilters({ ...filters, bands: v })} testid="filter-band" />
                <MultiSelect label="Locations" options={facets.locations} values={filters.locations} onChange={(v) => setFilters({ ...filters, locations: v })} testid="filter-location" />
                <MultiSelect label="Employment Status" options={facets.statuses} values={filters.statuses} onChange={(v) => setFilters({ ...filters, statuses: v })} testid="filter-status" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">DOJ from</Label>
                    <Input type="date" value={filters.doj_from} onChange={(e) => setFilters({ ...filters, doj_from: e.target.value })} className="rounded-sm" data-testid="filter-doj-from" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">DOJ to</Label>
                    <Input type="date" value={filters.doj_to} onChange={(e) => setFilters({ ...filters, doj_to: e.target.value })} className="rounded-sm" data-testid="filter-doj-to" />
                  </div>
                </div>
              </div>
            )}

            {mode === "ids" && (
              <div className="space-y-3">
                <div className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 p-4">
                  <div className="flex items-center gap-3">
                    <FileArrowUp size={20} className="text-adb-navy" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Upload CSV of employee_codes</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Required column: <code className="text-adb-navy">employee_code</code>. Any extra columns
                        (e.g. <code>new_ctc</code>, <code>effective_date</code>) become per-employee template variables.
                      </div>
                    </div>
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={(e) => handleCsvUpload(e.target.files?.[0])}
                      className="text-xs"
                      data-testid="bulk-csv-input"
                    />
                  </div>
                  {csvSummary && (
                    <div className="mt-3 text-xs space-y-1">
                      <div className="flex items-center gap-2 text-emerald-700"><CheckCircle size={14} weight="fill" /> Matched {csvSummary.matched} employees</div>
                      {csvSummary.unmatched > 0 && (
                        <div className="flex items-start gap-2 text-amber-700">
                          <Warning size={14} weight="fill" className="mt-0.5" />
                          <div>
                            <div>{csvSummary.unmatched} codes did not match:</div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{csvSummary.unmatchedCodes.slice(0, 15).join(", ")}{csvSummary.unmatchedCodes.length > 15 && "…"}</div>
                          </div>
                        </div>
                      )}
                      {sampleVars.length > 0 && (
                        <div className="text-zinc-600">Per-employee variables detected: <span className="font-mono">{sampleVars.join(", ")}</span></div>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-zinc-500">
                  Or leave CSV empty and pick employees from the preview list below (use the "Refresh preview" button on the full active roster).
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button onClick={previewRecipients} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="bulk-preview-btn" disabled={previewLoading}>
                <Users size={16} className="mr-1.5" />
                {previewLoading ? "Loading…" : "Refresh preview"}
              </Button>
              {recipients.length > 0 && (
                <>
                  <button onClick={() => toggleAll(true)} className="text-xs text-emerald-700 hover:underline" data-testid="bulk-select-all">Select all</button>
                  <span className="text-zinc-300">·</span>
                  <button onClick={() => toggleAll(false)} className="text-xs text-zinc-600 hover:underline" data-testid="bulk-select-none">Clear</button>
                </>
              )}
            </div>

            {truncated && (
              <div className="text-xs rounded-sm border border-amber-300 bg-amber-50 text-amber-800 p-2">
                Preview is limited to 500 recipients. Narrow the filters if you need to target a smaller group.
              </div>
            )}

            {recipients.length > 0 && (
              <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid="bulk-recipients-list">
                <table className="hr-table">
                  <thead>
                    <tr>
                      <th className="w-10"><Checkbox checked={recipients.every((r) => selectedIds[r.id])} onCheckedChange={(v) => toggleAll(!!v)} /></th>
                      <th>Employee</th>
                      <th>Dept</th>
                      <th>Grade / Band</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.id}>
                        <td><Checkbox checked={!!selectedIds[r.id]} onCheckedChange={(v) => setSelectedIds({ ...selectedIds, [r.id]: !!v })} data-testid={`bulk-recipient-${r.employee_code}`} /></td>
                        <td>
                          <div className="text-sm font-medium">{r.name}</div>
                          <div className="text-xs font-mono text-zinc-500">{r.employee_code}</div>
                        </td>
                        <td className="text-xs">{r.department || "—"}</td>
                        <td className="text-xs">{[r.grade, r.band].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="text-xs">{r.location || "—"}</td>
                        <td>
                          <Badge variant="outline" className={`rounded-sm text-[10px] ${CHIP_TONES[r.status] || CHIP_TONES.default}`}>{r.status || "—"}</Badge>
                        </td>
                        <td className="text-xs">
                          {r.email ? <span className="text-zinc-700">{r.email}</span>
                            : <span className="text-rose-600">no email</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-sm text-zinc-600">
              <strong>{selectedCount}</strong> selected · <span className="text-zinc-500">{recipients.length} in preview</span>
            </div>
          </div>

          {/* Variables */}
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div>
              <div className="text-sm font-semibold text-zinc-700">Template variables</div>
              <div className="text-xs text-zinc-500">
                Applied globally to every recipient. Use placeholders like <code>{`{{ effective_date }}`}</code> or <code>{`{{ new_ctc }}`}</code> in your template body.
                Per-employee overrides come from your CSV.
              </div>
            </div>
            <div className="space-y-2" data-testid="bulk-global-vars">
              {globalVars.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="variable_name (e.g. new_ctc)" value={row.k} onChange={(e) => { const next = [...globalVars]; next[i].k = e.target.value; setGlobalVars(next); }} className="rounded-sm max-w-xs font-mono text-xs" />
                  <Input placeholder="value" value={row.v} onChange={(e) => { const next = [...globalVars]; next[i].v = e.target.value; setGlobalVars(next); }} className="rounded-sm" />
                  <Button variant="outline" size="icon" onClick={() => setGlobalVars(globalVars.filter((_, idx) => idx !== i))} className="rounded-sm">
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setGlobalVars([...globalVars, { k: "", v: "" }])} className="rounded-sm" data-testid="add-global-var-btn">
                <Plus size={14} className="mr-1" /> Add variable
              </Button>
            </div>
            <div className="text-xs text-zinc-500">
              Auto-provided variables (no need to add):
              <span className="font-mono ml-1">employee_name, employee_code, designation, department, grade, band, location, joining_date, today, current_ctc, tenant_name</span>
              <div className="mt-1" data-testid="bulk-ctc-hint">CTC table: put <span className="font-mono">{"{{ ctc_table }}"}</span> in the template and supply <span className="font-mono">annual_ctc</span> (global or per-employee via CSV) — Basic, HRA, allowances, bonuses, PF, ESIC and take-home are computed by formula (configure in Letter Studio → CTC table).</div>
            </div>
          </div>
        </TabsContent>

        {/* ─── STEP 2 ───────────────────────────────────────────── */}
        <TabsContent value="deliver" className="mt-4 space-y-6">
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-4">
            <div className="text-sm font-semibold text-zinc-700">Delivery channels</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className={`rounded-sm border p-3 cursor-pointer ${deliver.pdf_vault ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`}>
                <div className="flex items-center gap-2">
                  <Checkbox checked={deliver.pdf_vault} onCheckedChange={(v) => setDeliver({ ...deliver, pdf_vault: !!v })} data-testid="deliver-pdf" />
                  <div>
                    <div className="text-sm font-medium">PDF to Documents</div>
                    <div className="text-xs text-zinc-500">Save to each employee's letter vault</div>
                  </div>
                </div>
              </label>
              <label className={`rounded-sm border p-3 cursor-pointer ${deliver.email ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`}>
                <div className="flex items-center gap-2">
                  <Checkbox checked={deliver.email} onCheckedChange={(v) => setDeliver({ ...deliver, email: !!v })} data-testid="deliver-email" />
                  <div>
                    <div className="text-sm font-medium">Email via Zenith360</div>
                    <div className="text-xs text-zinc-500">Notify with a portal link (no attachment)</div>
                  </div>
                </div>
              </label>
              <label className={`rounded-sm border p-3 cursor-pointer ${deliver.notification ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`}>
                <div className="flex items-center gap-2">
                  <Checkbox checked={deliver.notification} onCheckedChange={(v) => setDeliver({ ...deliver, notification: !!v })} data-testid="deliver-notification" />
                  <div>
                    <div className="text-sm font-medium">Dashboard notification</div>
                    <div className="text-xs text-zinc-500">In-app bell alert</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email subject override (optional)</Label>
                <Input value={subjectOverride} onChange={(e) => setSubjectOverride(e.target.value)} placeholder="Defaults to the letter title" className="rounded-sm" data-testid="subject-override" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Extra note above the portal link (optional)</Label>
                <Input value={emailPrefix} onChange={(e) => setEmailPrefix(e.target.value)} placeholder="e.g. Please review by 15 April." className="rounded-sm" data-testid="email-prefix" />
              </div>
            </div>
          </div>

          <div className="rounded-sm border border-adb-navy/20 bg-adb-navy/5 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="text-sm">
              Ready to issue <strong>{templates.find((t) => t.key === templateKey)?.title || templateKey}</strong> to <strong>{selectedCount}</strong> employee(s).
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={previewRender} className="rounded-sm" data-testid="preview-render-btn">
                <Eye size={16} className="mr-1.5" /> Preview letter
              </Button>
              <Button onClick={submitIssue} disabled={issuing || selectedCount === 0} className="btn-zenith rounded-sm" data-testid="bulk-issue-btn">
                <PaperPlaneTilt size={16} className="mr-1.5" />
                {issuing ? "Issuing…" : `Issue ${selectedCount} letters`}
              </Button>
            </div>
          </div>

          {lastRun && (
            <div className="border border-emerald-300 rounded-sm p-4 bg-emerald-50" data-testid="bulk-result">
              <div className="text-sm font-semibold text-emerald-900 mb-2">Bulk run complete · Batch <span className="font-mono">{lastRun.batch_id.slice(0, 8)}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <StatCard label="Generated" value={lastRun.counts.generated} tone="border-emerald-300 bg-white" />
                <StatCard label="Emails sent" value={lastRun.counts.email_sent} tone="border-emerald-300 bg-white" />
                <StatCard label="Emails failed / skipped" value={lastRun.counts.email_failed + lastRun.counts.email_skipped} tone="border-amber-300 bg-white" />
                <StatCard label="Notifications" value={lastRun.counts.notified} tone="border-emerald-300 bg-white" />
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-emerald-800">Per-recipient details ({lastRun.recipients.length})</summary>
                <table className="hr-table mt-2">
                  <thead><tr><th>Code</th><th>Name</th><th>Email</th><th>Email status</th><th>Notif</th></tr></thead>
                  <tbody>
                    {lastRun.recipients.map((r, i) => (
                      <tr key={i}>
                        <td className="font-mono text-[11px]">{r.employee_code}</td>
                        <td>{r.name}</td>
                        <td className="text-[11px]">{r.email || "—"}</td>
                        <td className="text-[11px]"><Badge variant="outline" className="rounded-sm text-[10px]">{r.email_status}</Badge></td>
                        <td className="text-[11px]"><Badge variant="outline" className="rounded-sm text-[10px]">{r.notif_status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </div>
          )}
        </TabsContent>

        {/* ─── HISTORY ───────────────────────────────────────────── */}
        <TabsContent value="history" className="mt-4">
          <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto" data-testid="bulk-history-list">
            <table className="hr-table">
              <thead>
                <tr><th>Started</th><th>Template</th><th>Issuer</th><th>Recipients</th><th>Emails</th><th>Notifications</th><th>Mode</th></tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">No bulk issuances yet.</td></tr>
                )}
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="text-xs font-mono">{new Date(h.started_at).toLocaleString()}</td>
                    <td><Badge variant="outline" className="rounded-sm bg-zinc-50 font-mono text-xs">{h.template_key}</Badge></td>
                    <td className="text-xs">{h.issued_by_email || h.issued_by}</td>
                    <td className="text-xs">{h.recipient_count}</td>
                    <td className="text-xs">{(h.counts?.email_sent ?? 0)} sent · {(h.counts?.email_failed ?? 0) + (h.counts?.email_skipped ?? 0)} skip/fail</td>
                    <td className="text-xs">{h.counts?.notified ?? 0}</td>
                    <td className="text-xs">{h.mode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview letter dialog */}
      <Dialog open={renderOpen} onOpenChange={setRenderOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Letter preview
              {renderData?.employee && <span className="ml-2 font-mono text-sm text-zinc-500">· {renderData.employee.name}</span>}
            </DialogTitle>
          </DialogHeader>
          {renderData && (
            <div className="space-y-4">
              <div className="text-lg font-semibold text-adb-navy">{renderData.title}</div>
              <div className="border border-zinc-200 rounded-sm p-6 bg-white text-sm leading-6 text-zinc-800 [&_table]:w-full [&_table]:my-3 [&_td]:py-1 [&_td]:px-2 [&_th]:py-1 [&_th]:px-2 [&_th]:bg-zinc-100 [&_td]:border [&_td]:border-zinc-200 [&_th]:border [&_th]:border-zinc-200" dangerouslySetInnerHTML={{ __html: renderData.body_html }} />
              <details className="text-xs text-zinc-500">
                <summary className="cursor-pointer">Variables used</summary>
                <pre className="font-mono bg-zinc-50 border border-zinc-200 rounded-sm p-2 mt-1 overflow-auto">{JSON.stringify(renderData.context_used, null, 2)}</pre>
              </details>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenderOpen(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
